import {
  Alert,
  Avatar,
  Card,
  CardContent,
  Link,
  Tag,
  Typography,
  useLocaleFormat,
} from '@eye-able/ui';
import BoundingBoxPreview from '@reportComponents/images/BoundingBoxPreview';
import { Check, ChevronDown, ChevronUp, RotateCcw, X } from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';

import LoadingButton from '@components/Button/LoadingButton';
import CodeBlockUntyped from '@components/CodeBlock';
import { getInitials } from '@helpers/functions';
import { resolveTeamColor } from '@pages/organisation/teams/teamColors';
import type {
  GovernanceIssueResponse,
  PolicyViolationLabel,
} from '@http/queries/useFetchGovernanceIssues';
import type { UpdatableGovernanceIssueStatus } from '@http/queries/useUpdateGovernanceIssueStatus';

import { STATUS_LABELS, STATUS_TAG_VARIANTS } from './constants';
import PrioritySignal from '../PrioritySignal';
import { getPriorityTranslationKey } from '../priority';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useGovernanceAccess } from '@/hooks/useGovernanceAccess';
import { formatIssueDetail } from './formatIssueDetail';

const CodeBlock = CodeBlockUntyped as ComponentType<{
  children?: ReactNode;
}>;

export type IssueTeam = {
  name: string;
  color: string | null;
};

function IssueCard({
  issue,
  team,
  policyViolationLabel,
  pendingStatus,
  onStatusChange,
}: {
  issue: GovernanceIssueResponse;
  team?: IssueTeam;
  policyViolationLabel: PolicyViolationLabel | undefined;
  pendingStatus: UpdatableGovernanceIssueStatus | null;
  onStatusChange: (
    issue: GovernanceIssueResponse,
    status: UpdatableGovernanceIssueStatus,
  ) => void;
}) {
  const { t } = useInlineTranslation();
  const { canEdit } = useGovernanceAccess(issue.auditEntryId);
  const { formatDate } = useLocaleFormat();
  const [showAllUrls, setShowAllUrls] = useState(false);
  const [showElementDetails, setShowElementDetails] = useState(false);
  const hasElementDetails = Boolean(issue.xPath || issue.htmlElement);
  const [firstUrl, ...otherUrls] = issue.urls ?? [];
  const isUpdating = pendingStatus !== null;
  const isIgnored = issue.status === 'ignored';
  const isFixed = issue.status === 'fixed';
  const nextIgnoreStatus: UpdatableGovernanceIssueStatus = isIgnored
    ? 'open'
    : 'ignored';
  const nextFixedStatus: UpdatableGovernanceIssueStatus = isFixed
    ? 'open'
    : 'fixed';

  return (
    <Card>
      <CardContent className='eye:flex eye:flex-col eye:gap-2xs eye:p-s'>
        <div className='eye:flex eye:items-start eye:gap-s'>
          <div className='eye:flex eye:shrink-0 eye:flex-col eye:gap-4xs'>
            <BoundingBoxPreview
              screenshotUrl={issue.screenshotPath}
              boundingBox={issue.boundingBox}
              alt={t('screenshotOf', { title: issue.policy })}
            />
            {hasElementDetails ? (
              <Link asChild size='small' className='eye:self-start'>
                <button
                  type='button'
                  aria-expanded={showElementDetails}
                  onClick={() => setShowElementDetails((expanded) => !expanded)}
                  className='eye:inline-flex eye:items-center eye:gap-5xs'
                >
                  {showElementDetails
                    ? t('hideXpathAndHtml')
                    : t('showXpathAndHtml')}
                  {showElementDetails ? (
                    <ChevronUp aria-hidden className='eye:icon-small' />
                  ) : (
                    <ChevronDown aria-hidden className='eye:icon-small' />
                  )}
                </button>
              </Link>
            ) : null}
          </div>
          <div className='eye:flex eye:min-w-0 eye:flex-col eye:gap-4xs eye:w-full'>
            <div className='eye:flex eye:flex-wrap eye:gap-4xs'>
              <Tag variant='outline' className='eye:min-w-auto'>
                {issue.policy}
              </Tag>
              {team ? (
                <Tag
                  variant='outline'
                  className='eye:inline-flex eye:items-center eye:gap-4xs'
                >
                  <Avatar
                    style={{ backgroundColor: resolveTeamColor(team.color) }}
                    className='eye:size-[16px] eye:shrink-0 eye:rounded-sm eye:font-semibold eye:text-[10px] eye:text-white'
                  >
                    {getInitials(team.name)}
                  </Avatar>
                  {team.name}
                </Tag>
              ) : null}
              <Tag
                variant='outline'
                aria-label={t('priorityLabel', {
                  priority: t(getPriorityTranslationKey(issue.priority)),
                })}
                className='eye:inline-flex eye:items-center eye:gap-4xs eye:min-w-auto'
              >
                <PrioritySignal
                  priority={issue.priority}
                  className='eye:size-[16px]'
                />
                {t(getPriorityTranslationKey(issue.priority))}
              </Tag>
              {issue.status === 'open' ? null : (
                <Tag variant={STATUS_TAG_VARIANTS[issue.status]}>
                  {t(STATUS_LABELS[issue.status])}
                </Tag>
              )}
            </div>
            {firstUrl ? (
              <Link
                href={firstUrl}
                isExternal
                size='small'
                className='eye:break-all'
              >
                {firstUrl}
              </Link>
            ) : null}
            {showAllUrls
              ? otherUrls.map((url) => (
                  <Link
                    key={url}
                    href={url}
                    isExternal
                    size='small'
                    className='eye:break-all'
                  >
                    {url}
                  </Link>
                ))
              : null}
            {otherUrls.length > 0 ? (
              <Link asChild size='small' className='eye:self-start'>
                <button
                  type='button'
                  aria-expanded={showAllUrls}
                  onClick={() => setShowAllUrls((expanded) => !expanded)}
                  className='eye:inline-flex eye:items-center eye:gap-5xs'
                >
                  {showAllUrls
                    ? t('hideOtherPages')
                    : t('showMorePages', { count: otherUrls.length })}
                  {showAllUrls ? (
                    <ChevronUp aria-hidden className='eye:icon-small' />
                  ) : (
                    <ChevronDown aria-hidden className='eye:icon-small' />
                  )}
                </button>
              </Link>
            ) : null}
            <div>
              <Typography
                tag='span'
                variant='body-s'
                className='eye:text-grey-120'
              >
                {t('introduced')}
              </Typography>{' '}
              <Typography
                tag='time'
                variant='body-s-highlight'
                dateTime={new Date(issue.createdAt).toISOString()}
              >
                {formatDate(new Date(issue.createdAt), 'P')}
              </Typography>
            </div>
            <Alert variant='warning' className='eye:mt-4xs eye:body-s'>
              {formatIssueDetail(issue.matchedRule, policyViolationLabel, t)}
            </Alert>
            {canEdit ? (
              <div className='eye:mt-4xs eye:flex eye:flex-wrap eye:justify-end eye:gap-xs'>
                <LoadingButton
                  variant='outline'
                  size='sm'
                  loading={pendingStatus === nextIgnoreStatus}
                  disabled={isUpdating}
                  onClick={() => onStatusChange(issue, nextIgnoreStatus)}
                >
                  {isIgnored ? <RotateCcw aria-hidden /> : <X aria-hidden />}
                  {isIgnored ? t('unignore') : t('ignore')}
                </LoadingButton>
                <LoadingButton
                  variant='black'
                  size='sm'
                  loading={pendingStatus === nextFixedStatus}
                  disabled={isUpdating}
                  onClick={() => onStatusChange(issue, nextFixedStatus)}
                >
                  {isFixed ? <RotateCcw aria-hidden /> : <Check aria-hidden />}
                  {isFixed ? t('reopen') : t('markFixed')}
                </LoadingButton>
              </div>
            ) : null}
          </div>
        </div>
        {showElementDetails ? (
          <div className='eye:flex eye:min-w-0 eye:flex-col eye:gap-2xs'>
            {issue.xPath ? (
              <div>
                <strong>{t('xpathLabel')}:</strong>
                <Typography variant='body-s' className='eye:break-all'>
                  {issue.xPath}
                </Typography>
              </div>
            ) : null}
            {issue.htmlElement ? (
              <div>
                <strong>{t('htmlLabel')}:</strong>
                <CodeBlock>{issue.htmlElement}</CodeBlock>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default IssueCard;
